import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { HOME } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";

const NAV_LINKS = [
  { to: "/features", label: "Features", testId: HOME.navFeatures },
  { to: "/pricing", label: "Pricing", testId: HOME.navPricing },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={[
        "sticky top-0 z-50 w-full",
        "bg-background/80 backdrop-blur-md",
        "transition-[border-color,background-color] duration-300",
        scrolled ? "border-b border-border" : "border-b border-transparent",
      ].join(" ")}
    >
      <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between px-5 sm:px-8">
        <div className="flex items-center gap-8">
          <Logo />
          <nav className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={({ isActive }) =>
                  [
                    "rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors",
                    isActive
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  ].join(" ")
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-1.5">
          <ThemeToggle />
          {user ? (
            <>
              <Button asChild variant="ghost" size="sm"
                className="hidden h-9 rounded-lg px-3 text-[13px] font-medium text-muted-foreground hover:text-foreground md:inline-flex">
                <Link to="/dashboard" data-testid="nav-dashboard-link">Dashboard</Link>
              </Button>
              <Button onClick={() => supabase.auth.signOut()} variant="outline" size="sm"
                data-testid="nav-signout-btn"
                className="hidden h-9 rounded-lg px-3 text-[13px] font-medium md:inline-flex">
                Sign out
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm"
                className="hidden h-9 rounded-lg px-3 text-[13px] font-medium text-muted-foreground hover:text-foreground md:inline-flex">
                <Link to="/login" data-testid={HOME.navSignIn}>Sign in</Link>
              </Button>
              <Button asChild size="sm"
                className="hidden h-9 rounded-lg bg-primary px-3.5 text-[13px] font-medium text-primary-foreground shadow-sm hover:bg-primary/90 md:inline-flex">
                <Link to="/workspace" data-testid={HOME.navGetStarted}>
                  Try free
                </Link>
              </Button>
            </>
          )}

          <Button
            data-testid={HOME.navMobileMenuBtn}
            variant="ghost"
            size="icon"
            aria-label="Open menu"
            onClick={() => setMobileOpen((v) => !v)}
            className="h-9 w-9 rounded-lg md:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-border bg-background md:hidden">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-1 px-5 py-4 sm:px-8">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`${link.testId}-mobile`}
                className={({ isActive }) =>
                  [
                    "rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                  ].join(" ")
                }
              >
                {link.label}
              </NavLink>
            ))}
            <div className="mt-2 grid grid-cols-2 gap-2 border-t border-border pt-3">
              <Button asChild variant="outline" className="h-10 rounded-lg">
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild className="h-10 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/signup">Get started</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
